import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface FormularioSecao {
  titulo: string;
  campos: { chave: string; valor: string }[];
}

@Component({
  selector: 'app-visualizar-formulario',
  templateUrl: './visualizar-formulario.component.html',
  styleUrls: ['./visualizar-formulario.component.scss']
})
export class VisualizarFormularioComponent implements OnInit {
  idFormulario = '';
  idTipoConsulta = '';
  loading = false;
  secoes: FormularioSecao[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ajaxService: AjaxService,
    private util: UtilService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.idFormulario = params.get('idFormulario') || '';
      this.idTipoConsulta = params.get('idTipoConsulta') || '';
      this.carregarFormulario();
    });
  }

  voltar(): void {
    this.router.navigate(['/consultar-atendimento']);
  }

  private carregarFormulario(): void {
    if (!this.idFormulario || !this.idTipoConsulta) {
      this.secoes = [];
      return;
    }

    const url = `${API}empresa/Formularios/VisualizarFullModal?id_formulario=${encodeURIComponent(this.idFormulario)}&idTipoConsulta=${encodeURIComponent(this.idTipoConsulta)}`;
    this.loading = true;
    this.util.showLoading();
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.secoes = [];
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível carregar o formulário.');
          return;
        }

        const data = this.util.converterParaLowercase(response?.data || {});
        this.secoes = this.montarSecoes(data);
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.secoes = [];
        this.util.toasterShowError('Erro', 'Erro ao carregar dados do formulário.');
      }
    });
  }

  private montarSecoes(data: any): FormularioSecao[] {
    if (!data || typeof data !== 'object') return [];

    const secoes: FormularioSecao[] = [];
    const entries = Object.entries(data);

    for (const [chave, valor] of entries) {
      if (valor === null || valor === undefined) continue;

      if (Array.isArray(valor)) {
        if (!valor.length) continue;
        const campos = valor.map((item, idx) => ({
          chave: `Item ${idx + 1}`,
          valor: this.resumirItem(item)
        }));
        secoes.push({ titulo: this.formatarTitulo(chave), campos });
        continue;
      }

      if (typeof valor === 'object') {
        const camposObjeto = this.extrairCamposObjeto(valor);
        if (camposObjeto.length) {
          secoes.push({ titulo: this.formatarTitulo(chave), campos: camposObjeto });
        }
        continue;
      }

      const valorTexto = this.formatarValor(valor);
      if (valorTexto) {
        secoes.push({
          titulo: this.formatarTitulo(chave),
          campos: [{ chave: this.formatarTitulo(chave), valor: valorTexto }]
        });
      }
    }

    return secoes;
  }

  private extrairCamposObjeto(obj: any): { chave: string; valor: string }[] {
    const campos: { chave: string; valor: string }[] = [];
    for (const [chave, valor] of Object.entries(obj || {})) {
      const valorTexto = this.formatarValor(valor);
      if (!valorTexto) continue;
      campos.push({ chave: this.formatarTitulo(chave), valor: valorTexto });
    }
    return campos;
  }

  private resumirItem(item: any): string {
    if (item === null || item === undefined) return '-';
    if (typeof item !== 'object') return this.formatarValor(item);
    const partes: string[] = [];
    for (const [chave, valor] of Object.entries(item)) {
      const valorTexto = this.formatarValor(valor);
      if (!valorTexto) continue;
      partes.push(`${this.formatarTitulo(chave)}: ${valorTexto}`);
      if (partes.length >= 4) break;
    }
    return partes.length ? partes.join(' | ') : '-';
  }

  private formatarValor(valor: any): string {
    if (valor === null || valor === undefined) return '';
    if (typeof valor === 'string') {
      const texto = valor.trim();
      return texto ? texto : '';
    }
    if (typeof valor === 'number') return String(valor);
    if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
    if (valor instanceof Date) return valor.toLocaleDateString('pt-BR');
    return '';
  }

  private formatarTitulo(chave: string): string {
    return String(chave || '')
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
}
